import { db } from '../firebase/firebase';
import { collection, getDocs, doc, updateDoc, getDoc, where, query, writeBatch, arrayRemove, arrayUnion } from 'firebase/firestore';
import { IPin } from '../types/Pin';
import { IRecording } from '../types/Recording';
import { TagManager } from './TagManager';
import { ProjectManager } from './ProjectManager';
import authenticationStore from '../stores/AuthenticationStore';
import { deleteFile } from '../firebase/storage';
import { withDemoModeFallback, isDemoModeWithTutorial, getDemoOrganisationIfActive } from './base/demoModeUtils';
import { DemoDataManager } from '../services/demoDataManager';
import { emitFirestoreRead } from '../utils/firestoreReadTelemetry';


export const PinManager = {
  pinIconCache: new Map<string, string>(),

  observePins: async (): Promise<IPin[]> => {
    try {
      const pinsRef = collection(db, 'Pins');
      const startedAt = Date.now();
      const querySnapshot = await getDocs(pinsRef);
      emitFirestoreRead({
        kind: 'getDocs',
        readTag: 'AdminPanel.PinManager.getAllPins',
        collectionName: 'Pins',
        docCount: querySnapshot.size,
        durationMs: Date.now() - startedAt,
        fromCache: querySnapshot.metadata.fromCache,
        hasPendingWrites: querySnapshot.metadata.hasPendingWrites
      });
      const pins: IPin[] = querySnapshot.docs.map(doc => doc.data() as IPin);
      return pins;
    } catch (error) {
      console.error("Error fetching pins: ", error);
      return [];
    }
  },


  updatePinLocation: async (pinKey: string, lat: number, lng: number): Promise<void> => {
    if (!pinKey) {
      console.error('pinKey is undefined.');
      return;
    }

    await withDemoModeFallback(
      async () => {
        const pin = DemoDataManager.getPin(pinKey);
        if (pin) {
          if (pin.location && !Array.isArray(pin.location) && pin.location.geoPoint) {
            pin.location.geoPoint.lat = lat;
            pin.location.geoPoint.long = lng;
            DemoDataManager.updatePin(pin);
          } else if (Array.isArray(pin.location) && pin.location[0]?.geoPoint) {
            // Handle twinned pins - update first location
            pin.location[0].geoPoint.lat = lat;
            pin.location[0].geoPoint.long = lng;
            DemoDataManager.updatePin(pin);
          }
        }
      },
      async () => {
        const pinRef = doc(db, 'Pins', pinKey);
        try {
          await updateDoc(pinRef, {
            'location.geoPoint.lat': lat, // Update the latitude
            'location.geoPoint.long': lng // Update the longitude
          });
        } catch (error) {
          console.error("Error updating pin location: ", error);
          throw error;
        }
      }
    );
  },
  getPin: async (pinKey: string): Promise<IPin | null> => {
    return await withDemoModeFallback(
      async () => DemoDataManager.getPin(pinKey),
      async () => {
        try {
      if (!pinKey || pinKey.trim() === '') {
        console.warn("Invalid pinKey provided:", pinKey);
        return null;
      }

      const pinRef = doc(db, 'Pins', pinKey);
      const pinDoc = await getDoc(pinRef);
      
      if (!pinDoc.exists()) {
        console.warn(`No pin found with key: ${pinKey}`);
        return null;
      }

      const pinData = pinDoc.data() as IPin;
      return {
        ...pinData,
        pinKey: pinDoc.id // Ensure pinKey is set
      };
      } catch (error) {
        console.error("Error fetching pin:", error);
        return null;
      }
      }
    );
  },

  removePin: async (pinKey: string): Promise<void> => {
    await withDemoModeFallback(
      async () => {
        DemoDataManager.deletePin(pinKey);
      },
      async () => {
        try {
      const pin = await PinManager.getPin(pinKey);
      if (!pin) {
        console.error(`Pin with key ${pinKey} not found`);
        return;
      }

      // Storage cleanup is best-effort and cannot join a Firestore batch
      if (pin.qrPath) {
        try {
          await deleteFile(pin.qrPath);
        } catch (error) {
          console.error(`Failed to delete QR code from storage: ${pin.qrPath}`, error);
        }
      }

      // Tag cleanup before the atomic pin ↔ project write
      let projectExists = false;
      if (pin.project) {
        const project = await ProjectManager.getProject(pin.project);
        projectExists = Boolean(project);
        if (project?.tags?.subjectTag) {
          for (const tagName of project.tags.subjectTag) {
            await TagManager.removeProjectTags(pin.project, tagName, pinKey);
          }
        }
      }

      // Unlink from project and delete pin in one batch
      const batch = writeBatch(db);
      if (pin.project && projectExists) {
        batch.update(doc(db, 'Projects', pin.project), {
          pins: arrayRemove(pinKey),
        });
      }
      batch.delete(doc(db, 'Pins', pinKey));
      await batch.commit();

      } catch (error) {
        console.error("Error removing pin:", error);
        throw error;
      }
      }
    );
  },

  getPinsByProjectKey: async (project: string, includeDemo: boolean = false): Promise<IPin[]> => {
    return await withDemoModeFallback(
      async () => {
        // Check if in organisational tutorial mode
        if (isDemoModeWithTutorial('organisational-projects-tutorial')) {
          // Ensure demo data is initialized
          const demoOrg = getDemoOrganisationIfActive();
          if (!demoOrg) {
            DemoDataManager.initializeOrganisationalDemoData();
          }
        }
        const pins = DemoDataManager.getPins(project);
        return pins;
      },
      async () => {
        try {
          const pinsRef = collection(db, 'Pins');
          const q = query(pinsRef, where('project', '==', project));
          const querySnapshot = await getDocs(q);
          let pins: IPin[] = querySnapshot.docs.map(doc => doc.data() as IPin);
          
          // Filter out demo pins if includeDemo is false
          if (!includeDemo) {
            pins = pins.filter(pin => !pin.isDemo);
          }
          
          return pins;
        } catch (error) {
          console.error("Error fetching pins by project key: ", error);
          throw error;
        }
      },
      async () => DemoDataManager.getPins(project)
    ).catch(() => []);
  },

  createPin: async (newPin: IPin): Promise<void> => {
    // If in demo mode, use localStorage instead of Firebase
    return await withDemoModeFallback(
      async () => {
        DemoDataManager.createPin({ ...newPin, isDemo: true });
      },
      async () => {

    try {
      const pinToSave = { ...newPin };
      let project = null as Awaited<ReturnType<typeof ProjectManager.getProject>>;

      if (newPin.project) {
        project = await ProjectManager.getProject(newPin.project);
        if (project?.isDemoProject) {
          pinToSave.isDemo = true;
          const { Timestamp } = await import('firebase/firestore');
          pinToSave.demoCreatedAt = Timestamp.now();
        }
      }

      // Create pin and link to project.pins in one batch
      const batch = writeBatch(db);
      batch.set(doc(db, 'Pins', pinToSave.pinKey), pinToSave);

      if (pinToSave.project) {
        batch.update(doc(db, 'Projects', pinToSave.project), {
          pins: arrayUnion(pinToSave.pinKey),
        });
      } else {
        console.warn("Pin created without associated project");
      }

      await batch.commit();

      if (pinToSave.project && project?.tags?.subjectTag) {
        for (const tagName of project.tags.subjectTag) {
          await TagManager.updateProjectTags(pinToSave.project, tagName, [pinToSave.pinKey]);
        }
      }
      } catch (error) {
        console.error("Error creating new pin: ", error);
        throw error;
      }
    });
  },

  updatePin: async (updatedPin: IPin): Promise<void> => {
    if (!updatedPin.pinKey) {
      console.error('pinKey is undefined.');
      return;
    }

    await withDemoModeFallback(
      async () => {
        DemoDataManager.updatePin(updatedPin);
      },
      async () => {
        const pinRef = doc(db, 'Pins', updatedPin.pinKey);
        try {
          // Here you would spread the updatedPin object to update all fields
          await updateDoc(pinRef, {
            ...updatedPin,
          });
        } catch (error) {
          console.error("Error updating pin: ", error);
          throw error;
        }
      }
    );
  },

  updateSequentialPinOrder: async (pins: IPin[]): Promise<void> => {
    await withDemoModeFallback(
      async () => {
        pins.forEach((pin) => {
          if (!pin.pinKey || !pin.pinOrder) return;
          DemoDataManager.updatePin(pin);
        });
      },
      async () => {
        const batch = writeBatch(db);

    pins.forEach((pin) => {
      if (!pin.pinKey || !pin.pinOrder) return;
      
      const pinRef = doc(db, 'Pins', pin.pinKey);

      // Update all pinOrder fields to ensure consistency
      batch.update(pinRef, { 
        pinOrder: {
          id: pin.pinOrder.id,
          branch: pin.pinOrder.branch,
          mainBranch: pin.pinOrder.mainBranch,
          subBranch: pin.pinOrder.subBranch,
          branchLevel: pin.pinOrder.branchLevel
        }
      });
    });

        try {
          await batch.commit();
        } catch (error) {
          console.error('❌ Error updating pin orders:', error);
          throw error;
        }
      }
    );
  },
  getTotalCollectionsByAlbumKey: async (albumKey: string): Promise<number> => {
    try {
      const querySnapshot = await getDocs(query(collection(db, 'Recordings'), where('albumKey', '==', albumKey)));
      let totalCollections = 0;
      querySnapshot.forEach((docSnapshot) => {
        const recording = docSnapshot.data() as IRecording;
        totalCollections += recording.collectionLog?.timesCollected || 0;
      });
      return totalCollections;
    } catch (error) {
      console.error('Error fetching total collections by albumKey:', error);
      return 0;
    }
  },

  getTotalPlaysByAlbumKey: async (albumKey: string): Promise<number> => {
    try {
      const querySnapshot = await getDocs(query(collection(db, 'Recordings'), where('albumKey', '==', albumKey)));
      let totalPlays = 0;
      querySnapshot.forEach((docSnapshot) => {
        const recording = docSnapshot.data() as IRecording;
        totalPlays += recording.collectionLog?.timesPlayed || 0;
      });
      return totalPlays;
    } catch (error) {
      console.error('Error fetching total plays by albumKey:', error);
      return 0;
    }
  },

  /** @deprecated Use getTotalCollectionsByAlbumKey */
  getTotalDownloadsByAlbumKey: async (albumKey: string): Promise<number> => {
    return PinManager.getTotalCollectionsByAlbumKey(albumKey);
  },

  getPinByAlbumKey: async (albumKey: string): Promise<IPin | null> => {
    if (!albumKey) {
      return null;
    }

    return await withDemoModeFallback(
      async () => {
        // Try to get pins from all projects by checking common project IDs
        // This is a workaround since getPins requires a projectId
        // In practice, we'd need to iterate through known projects or store pins differently
        // For now, we'll try to find it by checking if we can access the demo data directly
        try {
          const stored = localStorage.getItem('demo_pins');
          if (stored) {
            const data = JSON.parse(stored);
            const pinsMap = new Map(Object.entries(data));
            for (const pin of pinsMap.values()) {
              const pinData = pin as IPin;
              if (pinData.albumKey === albumKey) {
                return pinData;
              }
            }
          }
        } catch (error) {
          console.error('Error getting pin by albumKey in demo mode:', error);
        }
        return null;
      },
      async () => {
        try {
          const pinsRef = collection(db, 'Pins');
          const q = query(pinsRef, where('albumKey', '==', albumKey));
          const querySnapshot = await getDocs(q);
          
          if (querySnapshot.empty) {
            return null;
          }
          
          // Return the first pin found (there should typically only be one)
          const pinDoc = querySnapshot.docs[0];
          const pinData = pinDoc.data() as IPin;
          return {
            ...pinData,
            pinKey: pinDoc.id
          };
        } catch (error) {
          console.error("Error fetching pin by albumKey:", error);
          return null;
        }
      }
    );
  },

  updatePinCollectionLogic: async (pinKey: string, collectionLogic: string): Promise<void> => {
    if (!pinKey) {
      console.error('pinKey is undefined.');
      return;
    }
    if (!authenticationStore.isAuthenticated) {
      throw new Error('User must be authenticated to update pin collection logic');
    }
    const pinRef = doc(db, 'Pins', pinKey);
    try {
      await updateDoc(pinRef, { collectionLogic });
    } catch (error) {
      console.error("Error updating pin collection logic: ", error);
      throw error;
    }
  },

  validateTwinning: (pin: IPin): { isValid: boolean, errors: string[] } => {
    const errors: string[] = [];
    
    // Check if collectionLogic is regular (0 or null)
    if (pin.collectionLogic && pin.collectionLogic !== "0") {
      errors.push("Only regular pins (collectionLogic = 0) can be twinned");
    }
    
    // Check if location is an array
    if (!Array.isArray(pin.location)) {
      errors.push("Twinned pins must have location as an array");
      return { isValid: false, errors };
    }
    
    // Check if exactly 2 locations
    if (pin.location.length !== 2) {
      errors.push("Twinned pins must have exactly 2 locations");
    }
    
    // Check if locations have different coordinates
    if (pin.location.length === 2) {
      const [loc1, loc2] = pin.location;
      if (loc1.geoPoint && loc2.geoPoint) {
        const lat1 = parseFloat(loc1.geoPoint.lat.toString());
        const long1 = parseFloat(loc1.geoPoint.long.toString());
        const lat2 = parseFloat(loc2.geoPoint.lat.toString());
        const long2 = parseFloat(loc2.geoPoint.long.toString());
        
        if (lat1 === lat2 && long1 === long2) {
          errors.push("Twin locations must have different coordinates");
        }
        
        // Check for valid coordinates
        if (isNaN(lat1) || isNaN(long1) || isNaN(lat2) || isNaN(long2)) {
          errors.push("All locations must have valid latitude and longitude values");
        }
      } else {
        errors.push("Both locations must have valid geoPoint coordinates");
      }
    }
    
    return { isValid: errors.length === 0, errors };
  },
};
